import type { AfterResponseHook, BeforeErrorHook, BeforeRequestHook, HTTPError } from 'ky';

export type AppError = Error & {
	name: 'AppError';
	isApiError: true;
	source?: 'http' | 'network';
	status?: number;
	statusText?: string;
	url?: string;
	details?: unknown;
	isTimeout?: boolean;
	isAbort?: boolean;
	cause?: unknown;
};

export function createAppError(base: Partial<AppError>): AppError {
	const err = new Error('API_REQUEST_ERROR') as AppError;
	err.name = 'AppError';
	err.isApiError = true;
	Object.assign(err, base);
	return err;
}

export const kyBeforeRequestMock: BeforeRequestHook = async req => {
	// Dynamic import to avoid circular deps and support ESM bundlers
	const mod = await import('./mocks');
	mod.maybeThrowMockError(req);
};

export const kyAfterResponse: AfterResponseHook = async (_req, _opts, res) => {
	if (res.ok) return res;
	let details: unknown = undefined;
	try {
		const type = res.headers.get('content-type') || '';
		details = type.includes('application/json') ? await res.clone().json() : await res.clone().text();
	} catch {
		// ignore parse failures
	}
	throw createAppError({
		source: 'http',
		status: res.status,
		statusText: res.statusText,
		url: res.url,
		details,
	});
};

export const kyBeforeError: BeforeErrorHook = error => {
	type AppHttpError = HTTPError & {
		isApiError?: boolean;
		source?: 'http' | 'network';
		isTimeout?: boolean;
		isAbort?: boolean;
		cause?: unknown;
	};
	const e = error as unknown as AppHttpError;
	if (!e?.isApiError) {
		const isTimeout = error.name === 'TimeoutError';
		const isAbort = error.name === 'AbortError';
		e.name = 'AppError';
		e.isApiError = true;
		e.source = 'network';
		e.isTimeout = isTimeout;
		e.isAbort = isAbort;
	}
	return e as unknown as HTTPError;
};
